import { useDemoRequest } from "@/components/feature/DemoRequest";

interface PricingCard {
  name: string;
  tagline: string;
  bestFor: string;
  features: string[];
  priceLabel: string;
  cta: string;
  popular?: boolean;
}

const cards: PricingCard[] = [
  {
    name: "Start",
    tagline: "For businesses ready to digitize core operations.",
    bestFor: "Small and growing teams beginning their digital transformation journey.",
    features: [
      "Core business modules",
      "Essential reporting",
      "Employee and user management",
      "Basic workflow automation",
      "Standard support",
      "Fast deployment",
    ],
    priceLabel: "Tailored Pricing",
    cta: "Request Pricing",
  },
  {
    name: "Grow",
    tagline: "For organisations scaling operations, performance, and customer engagement.",
    bestFor: "Growing businesses that need more automation, visibility, and cross-functional collaboration.",
    features: [
      "Everything in Start",
      "Advanced modules",
      "Performance management",
      "Sales/CRM tools",
      "Growth/Marketing tools",
      "Workplace services",
      "Enhanced dashboards",
      "Priority support",
    ],
    priceLabel: "Custom Plan",
    cta: "Book Consultation",
    popular: true,
  },
  {
    name: "Transform",
    tagline: "For enterprises with complex processes and advanced requirements.",
    bestFor: "Larger organisations that require deep configuration, integrations, controls, and dedicated support.",
    features: [
      "Everything in Grow",
      "Advanced analytics",
      "Multi-branch support",
      "Custom workflows",
      "API integrations",
      "Role-based access control",
      "Dedicated account manager",
      "Implementation support",
      "Enterprise support",
    ],
    priceLabel: "Custom Plan",
    cta: "Speak with Sales",
  },
  {
    name: "Enterprise",
    tagline: "For large-scale organisations with mission-critical operations and bespoke infrastructure needs.",
    bestFor: "Multinational enterprises and government agencies requiring maximum scale, security, and complete operational sovereignty.",
    features: [
      "Everything in Transform",
      "Unlimited users & branches",
      "White-label across all modules",
      "On-premise or private cloud deployment",
      "Custom integrations & data residency",
      "99.9% SLA with 24/7 uptime guarantee",
      "Dedicated success team",
      "Quarterly business reviews",
      "Priority feature roadmap input",
      "Executive sponsorship program",
    ],
    priceLabel: "Tailored Engagement",
    cta: "Contact Enterprise",
  },
];

export default function PricingCards() {
  const { open } = useDemoRequest();

  return (
    <section className="relative py-16 md:py-24 bg-graphite-950">
      <div className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {cards.map((card) => {
              const isPopular = card.popular;
              const isEnterprise = card.name === "Enterprise";

              return (
                <div
                  key={card.name}
                  className={`relative glass-card flex flex-col ${
                    isPopular
                      ? "ring-1 ring-emerald-accent/40 border-emerald-border shadow-[0_0_60px_-16px_rgba(16,185,129,0.25)] bg-graphite-800/80"
                      : isEnterprise
                        ? "border-graphite-600/60 bg-graphite-800/60"
                        : "border-graphite-700/40 bg-graphite-800/40"
                  }`}
                >
                  {/* Popular glow bar */}
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-accent/40 via-emerald-accent to-emerald-accent/40 rounded-t-lg" />
                  )}

                  {/* Most Popular badge */}
                  {isPopular && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                      <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-emerald-accent text-graphite-900 text-xs font-semibold tracking-wide whitespace-nowrap">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-[10px]" />
                        </span>
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className={`p-6 md:p-7 flex flex-col flex-1 ${isPopular ? "pt-8" : ""}`}>
                    {/* Card header */}
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-white mb-2">{card.name}</h3>
                      <p className="text-sm text-silver-400 leading-relaxed">{card.tagline}</p>
                    </div>

                    {/* Price label */}
                    <div className="mb-6 pb-6 border-b border-graphite-700/50">
                      <span className="text-2xl lg:text-3xl font-bold text-white">{card.priceLabel}</span>
                    </div>

                    {/* Best for */}
                    <p className="text-xs text-silver-500 uppercase tracking-wider mb-4">Best For</p>
                    <p className="text-sm text-silver-300 leading-relaxed mb-6">{card.bestFor}</p>

                    {/* Features */}
                    <ul className="space-y-3 flex-1">
                      {card.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5 rounded-full bg-emerald-accent/10">
                            <i className="ri-check-line text-emerald-accent text-xs" />
                          </span>
                          <span className="text-sm text-silver-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <button
                      onClick={open}
                      className={`mt-8 w-full text-center text-sm font-semibold px-5 py-3 rounded-md transition-all whitespace-nowrap cursor-pointer ${
                        isPopular
                          ? "bg-emerald-accent text-graphite-900 hover:bg-emerald-hover"
                          : isEnterprise
                            ? "bg-graphite-700 text-white hover:bg-graphite-600 border border-graphite-500/40"
                            : "bg-graphite-700 text-white hover:bg-graphite-600 border border-graphite-600"
                      }`}
                    >
                      {card.cta}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}